import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Megaphone, X, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/contexts/AppContext";

/**
 * AnnouncementBanner — pinned strip that surfaces active admin announcements
 * on every page. Polls `/announcements/active?zip=` every 60s so newly-sent
 * broadcasts appear without a manual refresh.
 *
 * Tapping the strip opens a modal with the full body + optional CTA button.
 * Per-session dismiss keeps things quiet if a visitor doesn't care.
 */
const POLL_MS = 60_000;
const DISMISS_KEY = "budlink_ann_dismissed_v1";

const readDismissed = () => {
  try {
    const raw = sessionStorage.getItem(DISMISS_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
};
const writeDismissed = (set) => {
  try {
    sessionStorage.setItem(DISMISS_KEY, JSON.stringify(Array.from(set)));
  } catch {
    /* no-op */
  }
};

export const AnnouncementBanner = () => {
  const { location } = useApp();
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(readDismissed);
  const [open, setOpen] = useState(null); // announcement being read in modal
  const seenRef = useRef(new Set());

  const zip = location?.zip || "";

  const fetchNow = () => {
    api
      .get("/announcements/active", { params: zip ? { zip } : {} })
      .then((r) => {
        const rows = r.data?.results || [];
        setItems(rows);
      })
      .catch(() => setItems([]));
  };

  useEffect(() => {
    fetchNow();
    const t = setInterval(fetchNow, POLL_MS);
    return () => clearInterval(t);
  }, [zip]);

  const visible = items.filter((a) => !dismissed.has(a.id));
  const current = visible[0]; // show one at a time, newest first

  const dismiss = (id) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    writeDismissed(next);
  };

  const openModal = (ann) => {
    setOpen(ann);
    if (!seenRef.current.has(`click:${ann.id}`)) {
      api.post(`/announcements/${ann.id}/click`).catch(() => {});
      seenRef.current.add(`click:${ann.id}`);
    }
  };

  if (!current) return null;

  return (
    <>
      <div
        data-testid="announcement-banner"
        className="relative z-30 flex justify-center px-2 pt-2 pb-1"
      >
        <button
          type="button"
          onClick={() => openModal(current)}
          data-testid={`announcement-banner-${current.id}`}
          className="w-full max-w-md flex items-center gap-3 rounded-2xl border border-[#d4a268]/40 bg-[#1a1f19]/95 backdrop-blur-md px-3.5 py-2.5 shadow-lg hover:bg-[#232a20]/95 transition-colors text-left"
        >
          <div className="w-9 h-9 rounded-xl bg-[#d4a268]/15 border border-[#d4a268]/40 flex items-center justify-center shrink-0">
            <Megaphone className="w-4 h-4 text-[#d4a268]" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#d4a268]/80 truncate">
              Bud Link Announcement
            </div>
            <div className="text-sm text-white leading-snug line-clamp-1">
              {current.title}
            </div>
          </div>
          <span
            onClick={(e) => {
              e.stopPropagation();
              dismiss(current.id);
            }}
            aria-label="Dismiss"
            data-testid="announcement-dismiss"
            className="shrink-0 w-7 h-7 rounded-full text-white/60 hover:text-white hover:bg-white/10 flex items-center justify-center transition-colors cursor-pointer"
          >
            <X className="w-3.5 h-3.5" />
          </span>
        </button>
      </div>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent
          data-testid="announcement-modal"
          className="max-w-md"
        >
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-[#d4a268]" />
            {open?.title}
          </DialogTitle>
          <div className="mt-3 text-sm text-foreground/85 whitespace-pre-wrap leading-relaxed">
            {open?.body}
          </div>
          {open?.cta_url && (
            <a
              href={open.cta_url}
              target={open.cta_url.startsWith("http") ? "_blank" : undefined}
              rel="noopener noreferrer"
              data-testid="announcement-cta"
              onClick={() => {
                api.post(`/announcements/${open.id}/click`).catch(() => {});
              }}
              className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-5 text-sm"
            >
              {open?.cta_label || "Learn more"}
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AnnouncementBanner;
